export const getShowId = (show) => show?.id ?? show?.podcast_show_id ?? null;

// Nested campaign WebTrafficUrls are stored as `<showId>-e:<episodeUrl>`.
const EPISODE_URL_TAG = /^([a-zA-Z0-9]{5})-e:/;

export const untaggedEpisodeUrl = (url) => {
  if (typeof url !== "string") return "";

  return url.replace(EPISODE_URL_TAG, "");
};

export const taggedShowId = (url) => {
  if (typeof url !== "string") return null;

  const match = url.match(EPISODE_URL_TAG);
  return match ? match[1] : null;
};

// Orbit and the ad server hash the stored URL as-is, so a row only counts as a
// match when it carries the same URL the creative stores. Lowercasing or
// dropping a trailing slash here would let a reviewer approve an episode the
// backends will never resolve. Only the `<showId>-e:` tag is removed, because
// the creative stores the episode URL without it.
export const episodeUrlKey = (url) => {
  if (typeof url !== "string") return "";

  return untaggedEpisodeUrl(url).trim();
};

const showAndUrlKey = (showId, url) => `${showId}::${url}`;

export const flattenEpisodes = (podcastShows) => {
  if (!Array.isArray(podcastShows)) return [];

  return podcastShows.flatMap((show) => {
    const episodes = Array.isArray(show?.episodes) ? show.episodes : [];

    return episodes.map((episode) => ({
      ...episode,
      showId: getShowId(show),
      podcastName: show?.podcast_name ?? "",
    }));
  });
};

export const buildEpisodeIndex = (podcastShows) => {
  const byUrl = new Map();
  const byShowAndUrl = new Map();

  flattenEpisodes(podcastShows).forEach((episode) => {
    const url = episodeUrlKey(episode.episode_link);
    if (!url) return;

    if (!byUrl.has(url)) byUrl.set(url, episode);

    if (episode.showId != null && episode.showId !== "") {
      const key = showAndUrlKey(String(episode.showId), url);
      if (!byShowAndUrl.has(key)) byShowAndUrl.set(key, episode);
    }
  });

  return { byUrl, byShowAndUrl };
};

export const matchUrlRow = (row, index) => {
  if (!index) return null;

  const url = episodeUrlKey(row?.link);
  if (!url) return null;

  const showId = taggedShowId(row?.link);

  return showId
    ? index.byShowAndUrl?.get(showAndUrlKey(showId, url)) ?? null
    : index.byUrl.get(url) ?? null;
};

export const countShowsAndEpisodes = (podcastShows) => {
  if (!Array.isArray(podcastShows)) return { shows: 0, episodes: 0 };

  return {
    shows: podcastShows.length,
    episodes: podcastShows.reduce(
      (total, show) =>
        total + (Array.isArray(show?.episodes) ? show.episodes.length : 0),
      0
    ),
  };
};

export const formatShowsAndEpisodesCount = (podcastShows) => {
  const { shows, episodes } = countShowsAndEpisodes(podcastShows);

  return `${shows} ${shows === 1 ? "show" : "shows"} \u00b7 ${episodes} ${
    episodes === 1 ? "episode" : "episodes"
  }`;
};
